import { PbkHeaders } from '@pbk-web/data';
import { useTrainingPermissions } from '@pbk-web/main-training';
import { EventSystem, useCurrentClient, useCurrentTenantId } from '@pbk-web/state';
import type { RouteWithTitle } from '@pbk-web/types';
import { clientFeature } from '@pbk-web/types';
import { PageLoader } from '@pbk-web/ui';
import type { AxiosInstance } from 'axios';
import type { FC } from 'react';
import { createElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useShallow } from 'zustand/react/shallow';

import ErrorPage from './components/error/ErrorPage';
import PostHogFlagGate from './components/PostHogFlagGate';
import { usePbkAuth } from './contexts/PbkAuthContext';
import { useCurrentUser } from './global-state/Auth';
import { useErrors } from './global-state/Errors';
import usePermissions from './hooks/permissions/usePermissions';
import { useCurrentRoute } from './hooks/useCurrentRoute';
import { gqlState } from './indexWrapped';
import ErrorType from './models/Error';
import BaseService from './services/BaseService';
import ClientService from './services/ClientService';

const { FORBIDDEN } = ErrorType;

const ProtectedRouteWithTitle: FC<RouteWithTitle> = (props) => {
  const { features, translationKey, permissions, canOverridePermissions, requiresArchitect, component, postHogFlag } = props;
  const { t } = useTranslation('navigation');
  const title = translationKey ? t(translationKey) : '';
  const currentUser = useCurrentUser((x) => x.value);
  const location = useLocation();
  const hasPermission = usePermissions();
  const [currentTenantId, setCurrentTenantId] = useCurrentTenantId(useShallow((x) => [x.value, x.setValue]));
  const { match: routeMatch, id: routeId } = useCurrentRoute();
  const auth = usePbkAuth();
  const currentError = useErrors((x) => x.value);
  const { isTrainerOnAny, isLoadingTrainerPrograms } = useTrainingPermissions();

  useEffect(() => {
    document.title = `Playbook®${title ? ' - ' + title : ''}`;
  }, [title]);

  useEffect(() => {
    gqlState.getRefreshedToken = auth.refreshToken;
  }, [auth.refreshToken]);

  const [switchingToClientAccount, setSwitchingToClientAccount] = useState(true);
  const [switchingToDefaultAccount, setSwitchingToDefaultAccount] = useState(true);

  const [currentClient, setCurrentClient] = useCurrentClient(useShallow((x) => [x.value, x.setValue]));
  const [httpClientHasClient, setHttpClientHasClient] = useState(!!BaseService.httpClient.defaults.headers[PbkHeaders.ClientId]);

  useEffect(() => {
    const handler = (client: AxiosInstance) => {
      setHttpClientHasClient(!!client.defaults.headers[PbkHeaders.ClientId]);
    };

    EventSystem.listen('http-client-recreated', handler);
    return () => EventSystem.stopListening('http-client-recreated', handler);
  }, []);

  useEffect(() => {
    const switchAccount = async () => {
      setSwitchingToClientAccount(true);

      if (!auth.isAuthenticated || !currentUser?.id) {
        setSwitchingToClientAccount(false);
        return;
      }

      const routeClientId = routeMatch?.params.clientId;
      const outsideClientSpace = !routeClientId || routeClientId === 'undefined'; // "undefined" in the url is a string!

      const routeTenantId = routeMatch?.params.tenantId;
      const outsideTenantSpace = !routeTenantId || routeTenantId === 'undefined'; // "undefined" in the url is a string!

      if (outsideClientSpace && outsideTenantSpace) {
        setSwitchingToClientAccount(false);
        return;
      }

      gqlState.clientId = routeClientId as string;

      if (currentClient?.id !== routeClientId && !outsideClientSpace) {
        const clientRes = await ClientService.getClient(routeClientId);
        const currentTenantId = clientRes.tenantId;
        gqlState.tenantId = currentTenantId as string;

        setCurrentTenantId(currentTenantId);
        setCurrentClient(clientRes);
        BaseService.setClientTenantDetails(clientRes.id, clientRes.tenantId);
      } else if ((currentTenantId !== routeTenantId || outsideClientSpace) && !outsideTenantSpace) {
        setCurrentClient(null);
        setCurrentTenantId(routeTenantId);
        BaseService.setClientTenantDetails('', routeTenantId);
      }
      setSwitchingToClientAccount(false);
    };

    switchAccount();
  }, [
    routeMatch?.params.clientId,
    routeMatch?.params.tenantId,
    currentClient?.id,
    currentTenantId,
    auth.isAuthenticated,
    setCurrentTenantId,
    setCurrentClient,
    currentUser?.id,
  ]);

  const isAuthRoute = useMemo(() => location.pathname.startsWith('/auth'), [location.pathname]);

  const permissionOverrides = useMemo(() => {
    const overrides: Record<string, boolean> = {};
    overrides['training-lib'] = isTrainerOnAny;
    overrides['training'] = isTrainerOnAny;
    overrides['training-v2'] = isTrainerOnAny;
    overrides['my-org'] = isTrainerOnAny;
    overrides['client-form-builder'] = isTrainerOnAny;
    overrides['client-form-builder-v2-blank'] = isTrainerOnAny;
    return overrides;
  }, [isTrainerOnAny]);

  const isRouteFeatureEnabled = useMemo(() => {
    if (!features || features.length === 0) return true;

    return features.every((feature) => {
      switch (feature) {
        case clientFeature.AI_REVIEWS:
          return currentClient?.features.aiReview;
        default:
          return true;
      }
    });
  }, [features, currentClient?.features.aiReview]);

  useEffect(() => {
    const switchToDefaultAccount = async () => {
      setSwitchingToDefaultAccount(true);

      // Return if not authenticated or current user profile not fetched
      if (!auth.isAuthenticated || !currentUser?.id) {
        setSwitchingToDefaultAccount(false);
        return;
      }

      const routeClientId = routeMatch?.params.clientId;
      const outsideClientSpace = !routeClientId || routeClientId === 'undefined'; // "undefined" in the url is a string!

      const routeTenantId = routeMatch?.params.tenantId;
      const outsideTenantSpace = !routeTenantId || routeTenantId === 'undefined'; // "undefined" in the url is a string!

      if (outsideClientSpace && outsideTenantSpace && !isAuthRoute) {
        const defaultTenantId = currentUser?.defaultAccountTenantId;

        if (!defaultTenantId) {
          throw new Error('No default account id provider found');
        }

        setCurrentTenantId(defaultTenantId);
        setCurrentClient(null);
        BaseService.setClientTenantDetails('', defaultTenantId);
      }

      setSwitchingToDefaultAccount(false);
    };

    switchToDefaultAccount();
  }, [
    routeMatch?.params.clientId,
    isAuthRoute,
    routeMatch?.params.tenantId,
    auth.isAuthenticated,
    auth._implementationData.defaultAccountId,
    setCurrentTenantId,
    setCurrentClient,
    currentUser?.defaultAccountTenantId,
    auth._implementationData,
    auth.mode,
    currentUser?.id,
  ]);

  if (!auth.isAuthenticated) {
    if (!auth.isPending) auth.requireSignIn();
    return <PageLoader loading />;
  }

  if (!currentUser && currentError === null) {
    return <PageLoader loading />;
  }

  if (switchingToClientAccount || switchingToDefaultAccount) {
    return <PageLoader loading />;
  }

  // Trainer membership can override route permissions, so don't judge access before it's read.
  if (isLoadingTrainerPrograms && canOverridePermissions) {
    return <PageLoader loading />;
  }

  if (auth.features.clientHeader && routeMatch?.params.clientId && !httpClientHasClient) {
    return <PageLoader loading />;
  }

  if (
    (permissions?.length && !hasPermission(permissions) && !(canOverridePermissions && permissionOverrides[routeId])) ||
    (requiresArchitect && !currentUser?.isArchitect) ||
    !isRouteFeatureEnabled
  ) {
    return <ErrorPage type={FORBIDDEN} showGoBack showContactSupport />;
  }

  if (postHogFlag) {
    return <PostHogFlagGate flag={postHogFlag} component={component} />;
  }

  return createElement(component);
};

export default ProtectedRouteWithTitle;
